/**
 * React Query hooks for Actions and Groups
 */
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { apiGet } from '@/lib/api/axios';
import * as api from './api';
import * as meetingsApi from '../meetings/api';
import { AssociatedUser } from './api';
import type { OrganizationUser } from './types';

// Query keys
export const queryKeys = {
  groups: ['groups'] as const,
  group: (groupId: string) => ['groups', groupId, 'detail'] as const,
  groupMembers: (groupId: string) => ['groups', groupId, 'members'] as const,
  groupResources: (groupId: string) => ['groups', groupId, 'resources'] as const,
  groupExtractions: (groupId: string) => ['groups', groupId, 'extractions'] as const,
  groupExtractionsPreview: (groupId: string) => ['groups', groupId, 'extractions-preview'] as const,
  groupMeetings: (groupId: string) => ['groups', groupId, 'meetings'] as const,
  groupUpcomingMeetings: (groupId: string) => ['groups', groupId, 'upcoming-meetings'] as const,
  groupMuteStatus: (groupId: string) => ['groups', groupId, 'mute-status'] as const,
  groupAssociatedUsers: (groupId: string) => ['groups', groupId, 'associated-users'] as const,
  actionAssociatedUsers: (actionId: string) => ['actions', actionId, 'associated-users'] as const,
  tenantMembers: () => ['tenant-management', 'members'] as const,
  groupActions: (groupId: string) => ['groups', groupId, 'actions'] as const,
  groupHomeActions: (groupId: string) => ['groups', groupId, 'home-actions'] as const,
  groupMeetingAttendanceHeatmap: (groupId: string) => ['groups', groupId, 'meeting-attendance-heatmap'] as const,
  groupRecentActivity: (groupId: string) => ['groups', groupId, 'recent-activity'] as const,
  groupOverview: (groupId: string) => ['groups', groupId, 'overview'] as const,
  directoryOverview: ['groups', 'directory-overview'] as const,
  directorySnapshot: ['groups', 'directory-snapshot'] as const,
  catalogueDomains: ['catalogue-domains'] as const,
  safetyGroupRequirements: ['catalogue-safety-group-requirements'] as const
};

/** Refresh group roster and related hub views after members are added or changed. */
export function invalidateGroupMembership(queryClient: QueryClient, groupId: string) {
  void queryClient.invalidateQueries({ queryKey: queryKeys.groupMembers(groupId) });
  void queryClient.invalidateQueries({ queryKey: queryKeys.groupAssociatedUsers(groupId) });
  void queryClient.invalidateQueries({ queryKey: queryKeys.groupMeetingAttendanceHeatmap(groupId) });
  void queryClient.invalidateQueries({ queryKey: queryKeys.tenantMembers() });
  return invalidateGroupRecentActivity(queryClient, groupId);
}

/** Invalidate Home Activity Highlights after member/meeting/appointment/extraction changes. */
export function invalidateGroupRecentActivity(queryClient: QueryClient, groupId?: string | null) {
  void queryClient.invalidateQueries({ queryKey: queryKeys.directorySnapshot });
  if (groupId) {
    return queryClient.invalidateQueries({ queryKey: queryKeys.groupRecentActivity(groupId) });
  }
  return queryClient.invalidateQueries({
    predicate: (query) => query.queryKey[0] === 'groups' && query.queryKey[2] === 'recent-activity'
  });
}

export function useGroupOverview(groupId: string | undefined) {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: queryKeys.groupOverview(groupId!),
    enabled: !!groupId,
    queryFn: async () => {
      const current = await api.getGroupOverview(groupId!);
      if (current.is_fresh) return current;
      return api.refreshGroupOverview(groupId!);
    }
  });
  const refresh = useMutation({
    mutationFn: () => api.refreshGroupOverview(groupId!),
    onSuccess: (data) => {
      queryClient.setQueryData(queryKeys.groupOverview(groupId!), data);
    }
  });
  return { ...query, refresh };
}

const HUB_STALE_MS = 30_000;

export function useDirectorySnapshot(enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.directorySnapshot,
    queryFn: api.getDirectorySnapshot,
    enabled,
    staleTime: HUB_STALE_MS
  });
}

export function useDirectoryOverview(enabled: boolean) {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: queryKeys.directoryOverview,
    enabled,
    queryFn: async () => {
      const current = await api.getDirectoryOverview();
      if (current.is_fresh) return current;
      return api.refreshDirectoryOverview();
    }
  });
  const refresh = useMutation({
    mutationFn: () => api.refreshDirectoryOverview(),
    onSuccess: (data) => {
      queryClient.setQueryData(queryKeys.directoryOverview, data);
    }
  });
  return { ...query, refresh };
}

export function useGroup(groupId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.group(groupId!),
    queryFn: () => api.getGroup(groupId!),
    enabled: !!groupId,
    staleTime: HUB_STALE_MS,
    retry: (failureCount, error) => {
      const status = (error as { response?: { status?: number } })?.response?.status;
      if (status === 404 || status === 403) return false;
      return failureCount < 3;
    }
  });
}

export function useGroupActions(groupId: string | undefined, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: queryKeys.groupActions(groupId!),
    queryFn: () => api.getGroupActions(groupId!),
    enabled: !!groupId && (options?.enabled ?? true),
    staleTime: HUB_STALE_MS
  });
}

const HOME_ACTIONS_LIMIT = 8;

export function useGroupHomeActions(groupId: string | undefined, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: queryKeys.groupHomeActions(groupId!),
    queryFn: () => api.getGroupActions(groupId!, { openOnly: true, assignedToMe: true, limit: HOME_ACTIONS_LIMIT }),
    enabled: !!groupId && (options?.enabled ?? true),
    staleTime: HUB_STALE_MS
  });
}

export function useGroupExtractions(groupId: string | undefined, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: queryKeys.groupExtractions(groupId!),
    queryFn: () => api.getGroupExtractions(groupId!, { excludeMeetingLinked: true }),
    enabled: !!groupId && (options?.enabled ?? true),
    staleTime: HUB_STALE_MS
  });
}

export function useGroupExtractionsPreview(groupId: string | undefined, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: queryKeys.groupExtractionsPreview(groupId!),
    queryFn: () => api.getGroupExtractions(groupId!, { excludeMeetingLinked: true, includeItems: false }),
    enabled: !!groupId && (options?.enabled ?? true),
    staleTime: HUB_STALE_MS
  });
}

export function useGroupMeetings(
  groupId: string | undefined,
  options?: { enabled?: boolean; upcomingPerSeries?: number; completedPerSeries?: number }
) {
  const upcomingPerSeries = options?.upcomingPerSeries ?? meetingsApi.MEETING_LIST_INITIAL_UPCOMING;
  const completedPerSeries = options?.completedPerSeries ?? meetingsApi.MEETING_LIST_INITIAL_PAST;
  return useQuery({
    queryKey: [...queryKeys.groupMeetings(groupId!), upcomingPerSeries, completedPerSeries],
    queryFn: async () => {
      const [seriesData, meetingsData] = await Promise.all([
        meetingsApi.listMeetingSeries(groupId!),
        meetingsApi.listMeetings(groupId!, ['scheduled', 'completed'], {
          upcomingPerSeries,
          completedPerSeries
        })
      ]);
      return {
        series: seriesData.series,
        meetings: meetingsData.meetings,
        seriesCounts: meetingsData.series_counts ?? []
      };
    },
    enabled: !!groupId && (options?.enabled ?? true),
    staleTime: HUB_STALE_MS
  });
}

const HOME_MEETINGS_LIMIT = 5;

export function useGroupUpcomingMeetings(groupId: string | undefined, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: queryKeys.groupUpcomingMeetings(groupId!),
    queryFn: () => {
      const startDate = new Date().toISOString().slice(0, 10);
      return meetingsApi.listMeetings(groupId!, ['scheduled'], { startDate, limit: HOME_MEETINGS_LIMIT });
    },
    enabled: !!groupId && (options?.enabled ?? true),
    staleTime: HUB_STALE_MS
  });
}

export function useGroupMuteStatus(groupId: string | undefined, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: queryKeys.groupMuteStatus(groupId!),
    queryFn: () => api.getGroupReminderMuteStatus(groupId!),
    enabled: !!groupId && (options?.enabled ?? true),
    staleTime: HUB_STALE_MS
  });
}

export function useGroups(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: queryKeys.groups,
    queryFn: api.getGroups,
    enabled: options?.enabled ?? true,
    staleTime: HUB_STALE_MS
  });
}

export function useCatalogueDomains() {
  return useQuery({
    queryKey: queryKeys.catalogueDomains,
    queryFn: api.getCatalogueDomains,
    staleTime: 5 * 60_000
  });
}

export function useSafetyGroupRequirements(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: queryKeys.safetyGroupRequirements,
    queryFn: api.getSafetyGroupRequirements,
    enabled: options?.enabled ?? true,
    staleTime: 5 * 60_000
  });
}

export function useGroupResources(groupId: string | undefined, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: queryKeys.groupResources(groupId!),
    queryFn: () => api.getGroupResources(groupId!),
    enabled: !!groupId && (options?.enabled ?? true),
    staleTime: HUB_STALE_MS
  });
}

/**
 * Hook to fetch all users associated with a group.
 * Includes users from group permissions, action creators/assignees,
 * action log entries, and action updates.
 */
export function useGroupAssociatedUsers(groupId: string | undefined, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: queryKeys.groupAssociatedUsers(groupId!),
    queryFn: () => api.getGroupAssociatedUsers(groupId!),
    enabled: !!groupId && (options?.enabled ?? true),
    staleTime: HUB_STALE_MS
  });
}

/**
 * Hook to fetch associated users with fallback logic.
 * For group actions: tries group endpoint first, falls back to action endpoint on 404.
 * For personal actions: uses action endpoint directly.
 *
 * This handles cases where a user has access to a specific action but not the group.
 */
export function useAssociatedUsersWithFallback(
  groupId: string | undefined,
  actionId: string | undefined
): { data: AssociatedUser[]; isLoading: boolean } {
  const [shouldFallback, setShouldFallback] = useState(false);

  // Reset fallback state when groupId changes
  useEffect(() => {
    setShouldFallback(false);
  }, [groupId]);

  // Try group endpoint first if we have a groupId
  const groupQuery = useQuery({
    queryKey: queryKeys.groupAssociatedUsers(groupId!),
    queryFn: async () => {
      try {
        return await api.getGroupAssociatedUsers(groupId!);
      } catch (error: any) {
        // If 404/403, signal fallback to action endpoint
        if (error?.response?.status === 404 || error?.response?.status === 403) {
          setShouldFallback(true);
          return [] as AssociatedUser[];
        }
        throw error;
      }
    },
    enabled: !!groupId && !shouldFallback,
    retry: false // Don't retry on errors - we handle 404/403 specially
  });

  // Fallback to action endpoint if group endpoint failed or no groupId
  const actionQuery = useQuery({
    queryKey: queryKeys.actionAssociatedUsers(actionId!),
    queryFn: () => api.getActionAssociatedUsers(actionId!),
    enabled: !!actionId && (!groupId || shouldFallback)
  });

  // Determine which data to return
  if (groupId && !shouldFallback) {
    return {
      data: groupQuery.data ?? [],
      isLoading: groupQuery.isLoading
    };
  }

  return {
    data: actionQuery.data ?? [],
    isLoading: actionQuery.isLoading
  };
}

/** Tenant roster for assignee pickers (all active members). */
export function useTenantMembers(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: queryKeys.tenantMembers(),
    queryFn: async () => {
      const response = await apiGet<OrganizationUser[]>('/tenant-management/members', {
        meta: { globalError: false, context: 'tenant-members' }
      });
      return response.data;
    },
    enabled: options?.enabled ?? true
  });
}

/**
 * Users for ActionModal assignee dropdown: full tenant roster plus anyone
 * already tied to the action (so inactive or off-roster assignees still resolve).
 */
export function useActionModalUsers(
  groupId: string | undefined,
  actionId: string | undefined,
  options?: { enabled?: boolean }
): { users: OrganizationUser[]; isLoading: boolean } {
  const enabled = options?.enabled ?? true;
  const { data: tenantMembers = [], isLoading: tenantLoading } = useTenantMembers({ enabled });
  const { data: associatedUsers = [], isLoading: associatedLoading } = useAssociatedUsersWithFallback(
    enabled ? groupId : undefined,
    enabled ? actionId : undefined
  );

  const users = useMemo(() => {
    const userMap = new Map<number, OrganizationUser>();
    tenantMembers.forEach((u) => userMap.set(u.user_id, u));
    associatedUsers.forEach((u) => userMap.set(u.user_id, u));
    return Array.from(userMap.values());
  }, [tenantMembers, associatedUsers]);

  return { users, isLoading: tenantLoading || associatedLoading };
}

interface RefreshAssociatedUsersOnOpenOptions {
  groupId?: string | null;
  actionId?: string | null;
  getActionIds?: () => string[];
}

/**
 * Returns an onOpen handler that refreshes associated-user queries.
 * Use for assignee pickers so each open reflects current assignable users.
 */
export function useRefreshAssociatedUsersOnOpen({
  groupId,
  actionId,
  getActionIds
}: RefreshAssociatedUsersOnOpenOptions) {
  const queryClient = useQueryClient();

  return useCallback(() => {
    if (groupId) {
      void queryClient.invalidateQueries({ queryKey: queryKeys.groupAssociatedUsers(groupId) });
      return;
    }

    const actionIds = new Set<string>();
    if (actionId) actionIds.add(actionId);
    getActionIds?.().forEach((id) => actionIds.add(id));

    actionIds.forEach((id) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.actionAssociatedUsers(id) });
    });
  }, [actionId, getActionIds, groupId, queryClient]);
}
